// frontend/src/utils/calendarUtils.ts
// Lógica pura del calendario laboral editable. El array de CalendarDay son SOLO
// excepciones (festivos y findes-laborables); los días ausentes siguen la regla
// por defecto (L-V laborable, S-D no laborable). Reutiliza buildCalendarMap.
import type { CalendarDay } from '@/types/productionSequence';
import { buildCalendarMap } from './sequenceCalculations';

export type DayKind = 'laborable' | 'finde' | 'festivo' | 'finde-laborable';

/** Fecha ISO (YYYY-MM-DD, con o sin hora) → ¿es sábado/domingo? (UTC). */
export function isWeekendISO(date: string): boolean {
  const day = new Date(`${date.slice(0, 10)}T00:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
}

/** Clasifica un día para colorearlo según el override del calendario y el día de semana. */
export function dayKind(date: string, map: Map<string, boolean>): DayKind {
  const override = map.get(date.slice(0, 10));
  const weekend = isWeekendISO(date);
  if (override === false) return 'festivo';
  if (override === true) return weekend ? 'finde-laborable' : 'laborable';
  return weekend ? 'finde' : 'laborable';
}

/**
 * Alterna el estado laborable de un día (inmutable). Mantiene el array como
 * solo-excepciones: si el nuevo estado coincide con el defecto del día, quita la
 * entrada; si difiere, hace upsert conservando la etiqueta previa.
 */
export function toggleDayOverride(days: CalendarDay[], date: string): CalendarDay[] {
  const key = date.slice(0, 10);
  const map = buildCalendarMap(days);
  const defaultWorkday = !isWeekendISO(key);
  const effective = map.get(key) ?? defaultWorkday;
  const next = !effective;

  if (next === defaultWorkday) {
    // Vuelve al defecto → eliminar la excepción.
    return days.filter(d => d.date !== key);
  }
  const prev = days.find(d => d.date === key);
  const upserted: CalendarDay = { date: key, isWorkday: next, label: prev?.label };
  const rest = days.filter(d => d.date !== key);
  return [...rest, upserted];
}

/**
 * Celdas de un mes para renderizar una mini-rejilla 6×7 empezando en LUNES.
 * Devuelve 42 posiciones: fecha ISO "YYYY-MM-DD" o null para los huecos.
 */
export function buildMonthCells(year: number, month0: number): (string | null)[] {
  const first = new Date(Date.UTC(year, month0, 1));
  // getUTCDay: 0=domingo … 6=sábado. Queremos lunes(1) como columna 0.
  const lead = (first.getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const mm = String(month0 + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    cells.push(`${year}-${mm}-${dd}`);
  }
  while (cells.length < 42) cells.push(null);
  return cells;
}
